import React, { useEffect, useRef } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import { FiRepeat, FiUsers } from "react-icons/fi";
import "./AppCalendar.css";

const VIEW_LABELS = {
  dayGridMonth: "month",
  timeGridWeek: "week",
  timeGridDay: "day",
  listWeek: "schedule",
};

const AppCalendar = ({
  events = [],
  initialView = "dayGridMonth",
  onRangeChange,
  onEventClick,
  onDateClick,
  onDateSelect,
  onViewChange,
  height = "auto",
  selectable = false,
  focusDate,
}) => {
  const calendarRef = useRef(null);

  useEffect(() => {
    if (!focusDate) return;
    const date = new Date(focusDate);
    if (Number.isNaN(date.getTime())) return;
    calendarRef.current?.getApi?.().gotoDate(date);
  }, [focusDate]);

  const handleDatesSet = (info) => {
    onRangeChange?.({ start: info.start, end: info.end, viewType: info.view.type });
    onViewChange?.(info.view.type);
  };

  const handleEventClick = (info) => {
    info.jsEvent.preventDefault();
    onEventClick?.(info.event);
  };

  const handleDateClick = (info) => {
    onDateClick?.(info);
  };

  const handleDateSelect = (info) => {
    onDateSelect?.(info);
  };

  const renderEventContent = (info) => {
    const {
      isRecurring,
      statusClass,
      statusLabel,
      isCondensedRejectedRecurring,
      hasSlots,
      slotCount,
      openSlotCount,
      filledSlotCount,
    } = info.event.extendedProps;
    const showStatus = statusClass && statusClass !== "active";
    const showTime = info.timeText && !info.view?.type?.startsWith("list");
    const isMonthView = info.view?.type === "dayGridMonth";
    const showFooter =
      !isMonthView && (showStatus || isCondensedRejectedRecurring || hasSlots);

    return (
      <div className="app-calendar-event-content">
        <div className="app-calendar-event-mainline">
          {isRecurring && (
            <FiRepeat className="app-calendar-event-repeat-icon" aria-hidden />
          )}
          {showTime && (
            <span className="app-calendar-event-time">{info.timeText}</span>
          )}
          {hasSlots && isMonthView && (
            <span className="app-calendar-slot-pill app-calendar-slot-pill--compact">
              <FiUsers aria-hidden />
              {slotCount}
            </span>
          )}
          <span className="app-calendar-event-title">{info.event.title}</span>
        </div>
        {showFooter && (
          <div className="app-calendar-event-footer">
            {hasSlots && (
              <span className="app-calendar-slot-pill">
                <FiUsers aria-hidden />
                {openSlotCount} open / {filledSlotCount} filled
              </span>
            )}
            {showStatus && (
              <span className={`app-calendar-status-chip app-calendar-status-chip--${statusClass}`}>
                {statusLabel}
              </span>
            )}
            {isCondensedRejectedRecurring && (
              <span className="app-calendar-recurring-chip">shown once</span>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="app-calendar">
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
        initialView={initialView}
        headerToolbar={{
          // title furthest left, then ‹ › today
          left: "title prev,next today",
          center: "",
          right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
        }}
        buttonText={VIEW_LABELS}
        events={events}
        datesSet={handleDatesSet}
        eventClick={handleEventClick}
        dateClick={handleDateClick}
        select={handleDateSelect}
        eventContent={renderEventContent}
        height={height}
        selectable={selectable}
        selectMirror
        nowIndicator
        dayMaxEvents={3}
        moreLinkClick="popover"
        expandRows
        slotMinTime="06:00:00"
        slotMaxTime="27:00:00"
        eventTimeFormat={{
          hour: "numeric",
          minute: "2-digit",
          meridiem: "short",
        }}
      />
    </div>
  );
};

export default AppCalendar;
